// Reservations translations for React Admin resource
export const reservationsResource = {
  name: 'Reservation |||| Reservations',
  fields: {
    id: 'Reservation ID',
    status: 'Status',
    siteId: 'Site',
    chargerId: 'Charger',
    connectorId: 'Connector',
    appUserId: 'App User',
    vehicleId: 'Vehicle',
    requestedChargerTypeId: 'Charger Type',
    reservationMode: 'Mode',
    accessMode: 'Access',
    startTime: 'Start Time',
    slotEndAt: 'Slot End',
    holdUntil: 'Hold Until',
    durationMinutes: 'Duration',
    policyId: 'Policy',
    feeAmount: 'Fee Amount',
    feePolicy: 'Fee Policy',
    walletHoldAmount: 'Wallet Hold',
    walletHoldStatus: 'Hold Status',
    transactionId: 'Linked Transaction',
    modificationCount: 'Modifications',
    modifiedAt: 'Last Modified',
    cancelledAt: 'Cancelled At',
    cancelledBy: 'Cancelled By',
    cancelledReason: 'Cancellation Reason',
    adminNotes: 'Admin Notes',
    assignedAt: 'Assigned At',
    reassignmentCount: 'Reassignments',
    ocppReservationId: 'OCPP Reservation ID',
    createdAt: 'Created',
    updatedAt: 'Updated',
  },
};

// Custom reservations translations for components
export const reservations = {
  subtitle: 'Manage charging reservations',
  list: {
    title: 'Reservations',
    subtitle: 'Manage charging reservations',
    searchPlaceholder: 'Search reservations',
    noReservations: 'No reservations found',
  },
  show: {
    subtitle: 'Reservation details',
    sections: {
      summary: 'Summary',
      policySnapshot: 'Policy Snapshot',
      feeAndWallet: 'Fee & Wallet',
      linkedSession: 'Linked Charging Session',
      modifications: 'Modifications & Audit',
    },
    actions: {
      cancel: 'Cancel Reservation',
      captureNoShow: 'Capture No-Show',
      refreshAssignment: 'Refresh Assignment',
      forceReleaseHold: 'Force Release Hold',
      confirmCancel: 'Are you sure you want to cancel this reservation?',
      confirmCapture: 'Are you sure you want to capture the no-show fee?',
      confirmRefresh: 'Re-assign this reservation to a new charger?',
      confirmForceRelease: 'This will release the wallet hold without changing reservation status. Use only when reconciling stuck holds. Continue?',
      reasonPlaceholder: 'Optional reason or note',
    },
  },
  edit: {
    title: 'Edit Reservation',
    subtitle: 'Update reservation details',
    backToList: 'Back to reservations',
    adminNotes: 'Admin Notes',
    cancellationReason: 'Cancellation Reason',
    statusChangeWarning: 'Status changes are limited per the reservation lifecycle policy.',
  },
  statuses: {
    pending_payment: 'Pending Payment',
    confirmed: 'Confirmed',
    active: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
    no_show: 'No Show',
    expired: 'Expired',
  },
  modes: {
    fixed_slot: 'Fixed Slot',
    next_slot: 'Next Slot',
  },
  accessModes: {
    reservation_only: 'Reservation Only',
    reserve_or_fcfs: 'Reserve or First-Come-First-Served',
  },
  feePolicies: {
    none: 'No Fee',
    hold_release: 'Hold & Release',
    hold_apply: 'Hold & Apply',
    hold_forfeit_on_noshow: 'Hold (forfeit on no-show)',
  },
  feePolicyTooltips: {
    none: 'No reservation fee charged.',
    hold_release: 'Hold fee from wallet at booking; always released back after session or on no-show.',
    hold_apply: 'Hold fee from wallet at booking; applied as prepayment to the charging session cost.',
    hold_forfeit_on_noshow: 'Hold fee from wallet at booking; released if user attends, captured if no-show.',
  },
  walletHoldStatus: {
    pending: 'Pending',
    held: 'Held',
    released: 'Released',
    captured: 'Captured',
  },
  wallet: {
    timeline: 'Wallet Timeline',
    reasons: {
      hold: 'Hold placed',
      release: 'Hold released',
      apply: 'Fee applied to session',
      noshow_capture: 'Fee forfeited (no-show)',
    },
    types: {
      pre_auth: 'Pre-auth',
      reservation_fee_applied: 'Reservation fee applied',
      reservation_forfeit: 'Reservation forfeit',
    },
    statuses: {
      hold: 'Hold',
      released: 'Released',
      applied: 'Applied',
      forfeited: 'Forfeited',
    },
    empty: 'No wallet transactions for this reservation',
  },
  filters: {
    search: 'Search',
    allSites: 'All Sites',
    allChargers: 'All Chargers',
    allUsers: 'All Users',
    allStatuses: 'All Statuses',
    allPolicies: 'All Policies',
    fromDate: 'From',
    toDate: 'To',
    chargerNeedsSite: 'Pick a site first',
  },
  calendar: {
    title: 'Calendar View',
    selectSite: 'Select a site to view reservations',
    selectCharger: 'All chargers (optional)',
    allChargers: 'All chargers',
    dayView: 'Day',
    weekView: 'Week',
    today: 'Today',
    legend: 'Legend',
    legendConfirmed: 'Confirmed',
    legendActive: 'Active',
    legendNoShow: 'No-Show',
    legendCompleted: 'Completed',
    legendCancelled: 'Cancelled',
    noReservations: 'No reservations in this range',
    mobileHint: 'Calendar view is best on desktop',
    openDetail: 'Open full details',
  },
  policySnapshot: {
    title: 'Policy at booking time',
    readOnlyNote: 'This is the policy as it existed when this reservation was created. The live policy may differ.',
    reservationMode: 'Reservation Mode',
    accessMode: 'Access Mode',
    holdMinutes: 'Hold Window (minutes)',
    slotDurationMinutes: 'Slot Duration (minutes)',
    slotGranularityMinutes: 'Slot Granularity (minutes)',
    minLeadMinutes: 'Min Lead Time (minutes)',
    maxLeadMinutes: 'Max Lead Time (minutes)',
    maxActivePerUser: 'Max Active Per User',
    cancelWindowMinutes: 'Cancel Window (minutes)',
    modifyWindowMinutes: 'Modify Window (minutes)',
    maxModifications: 'Max Modifications',
    feePolicy: 'Fee Policy',
    noShowPenaltyAmount: 'No-Show Penalty',
    allowedMembershipLevels: 'Allowed Membership Levels',
    allowedUserGroupIds: 'Allowed User Groups',
    requireWalletBalance: 'Require Wallet Balance',
  },
  applicablePolicy: {
    title: 'Applicable Reservation Policy',
    none: 'No reservation policy is currently applicable for this entity.',
    viewFull: 'View full policy',
    priority: 'Priority',
    effectiveFrom: 'Effective From',
    effectiveUntil: 'Effective Until',
  },
  fee: {
    title: 'Fee Summary',
    walletHoldChip: 'Wallet hold',
    held: 'Held at booking',
    applied: 'Applied to session',
    forfeited: 'Forfeited (no-show)',
    refundReason: 'Refund reason',
  },
  linkedSession: {
    title: 'Linked charging session',
    transactionId: 'Transaction ID',
    sessionCost: 'Session cost',
    reservationApplied: 'Reservation applied',
    net: 'Net',
    none: 'No linked session yet',
  },
  modifications: {
    count: 'Modifications',
    lastModified: 'Last modified',
    cancelledAt: 'Cancelled at',
    cancelledBy: 'Cancelled by',
    cancelledReason: 'Cancellation reason',
    auditLog: 'Audit Log',
    auditEmpty: 'No audit entries for this reservation',
  },
  tooltips: {
    vendorScope: 'You can only see reservations for sites assigned to your merchant.',
    noShowPenalty: 'Captured from wallet hold when the user does not arrive within the hold window.',
    walletHold: 'Reservation fee held from the user wallet until the session completes.',
    policySnapshotReadOnly: 'This snapshot is immutable. The live policy may differ.',
  },
  refundLabel: {
    none: 'No fee',
    holdRelease: 'Fully refundable',
    holdApply: 'Applied to your charging cost',
    holdForfeit: 'Refundable if you arrive; forfeited if you do not show up',
  },
  notifications: {
    cancelled: 'Reservation cancelled',
    noShowCaptured: 'No-show fee captured',
    reassigned: 'Reservation re-assigned',
    holdReleased: 'Wallet hold force-released',
    updated: 'Reservation updated',
    error: {
      cancel: 'Failed to cancel reservation',
      capture: 'Failed to capture no-show fee',
      reassign: 'Failed to re-assign reservation',
      forceRelease: 'Failed to release hold',
      update: 'Failed to update reservation',
    },
  },
};
